import { FundItem } from "../models/Fund";
import { LeoDiversificationRate, LeoTakeUpRate } from "../resources/api-constants";

const cos = Math.cos;
const sin = Math.sin;
const π = Math.PI;
export const Utilities = {
    f_matrix_times: ([[a, b], [c, d]]: number[][], [x, y]: number[]): number[] => [a * x + b * y, c * x + d * y],
    f_rotate_matrix: (x: number): number[][] => [[cos(x), -sin(x)], [sin(x), cos(x)]],
    f_vec_add: ([a1, a2]: number[], [b1, b2]: number[]): number[] => [a1 + b1, a2 + b2],
    f_svg_ellipse_arc: ([cx, cy]: number[], [rx, ry]: number[], [t1, Δ]: number[], φ: number, lineToCenter?: boolean): string => {
        Δ = Δ % (2 * π);
        const rotMatrix = Utilities.f_rotate_matrix(φ);
        const [sX, sY] = (Utilities.f_vec_add(Utilities.f_matrix_times(rotMatrix, [rx * cos(t1), ry * sin(t1)]), [cx, cy]));
        const [eX, eY] = (Utilities.f_vec_add(Utilities.f_matrix_times(rotMatrix, [rx * cos(t1 + Δ), ry * sin(t1 + Δ)]), [cx, cy]));
        const fA = ((Δ > π) ? 1 : 0);
        const fS = ((Δ > 0) ? 1 : 0);
        return (lineToCenter ? "M " + cx + " " + cy + " L " : "M ") + sX + " " + sY + " A " + [rx, ry, φ / (2 * π) * 360, fA, fS, eX, eY].join(" ");
    },
    f_svg_rotate_coord: ([cx, cy]: number[], φ: number): number[] => {
        const rotMatrix = Utilities.f_rotate_matrix(φ);
        return Utilities.f_matrix_times(rotMatrix, [cx, cy]);
    },
    toFriendlyMoneyValue: (value: number): string => {
        if (value >= 1000000) {
            return `${Utilities.round(value / 1000000, 1)}M`;
        } else if (value >= 1000) {
            return `${Utilities.round(value / 1000, 1)}k`;
        }
        return `${value}`;
    },
    toCountdownDate: (date: Date): number => {
        const dateNum = date.getTime();
        const diff = dateNum - Date.now();
        return Math.floor(diff / (1000 * 3600 * 24));
    },
    distinct: (value: any, index: number, array: any[]): boolean => {
        return array.indexOf(value) === index;
    },
    isEven: (a: number): boolean => {
        return (a & 1) === 0;
    },
    toClassName: (text: string): string => {
        return text ? text.toLocaleLowerCase().replaceAll(/[^a-z0-9]/gi, '') : "";
    },
    round: function (num: number, decimalPlaces: number): number {
        const p = Math.pow(10, decimalPlaces || 0);
        const n = (num * p) * (1 + Number.EPSILON);
        return Math.round(n) / p;
    },
    // Decimal ceil
    ceil: function (num: number, decimalPlaces: number): number {
        const p = Math.pow(10, decimalPlaces || 0);
        const n = (num * p) * (1 - Math.sign(num) * Number.EPSILON);
        return Math.ceil(n) / p;
    },
    // Decimal floor
    floor: function (num: number, decimalPlaces: number): number {
        const p = Math.pow(10, decimalPlaces || 0);
        const n = (num * p) * (1 + Math.sign(num) * Number.EPSILON);
        return Math.floor(n) / p;
    },
    // Decimal trunc
    trunc: function (num: number, decimalPlaces: number): number {
        return (num < 0 ? this.ceil : this.floor)(num, decimalPlaces);
    },
    // Format using fixed-point notation
    toFixed: function (num: number, decimalPlaces: number): string {
        return this.round(num, decimalPlaces).toFixed(decimalPlaces);
    },
    shuffleArray: function <T>(array: Array<T>): Array<T> {
        const shuffledArray = [...array];
        for (let i = shuffledArray.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
        }
        return shuffledArray;
    },
    toUpperCamelCase: function (text: string): string {
        if (text.length === 0) {
            return text;
        }
        return text.substring(0, 1).toUpperCase() + text.substring(1);
    },
    getTakeUpRateValue: function (fund: FundItem): number {
        return fund.fundSize * LeoTakeUpRate;
    },
    getDiversificationRateValue: function (raising: number): number {
        return raising * LeoDiversificationRate;
    },
    getFundMaxValue: function (raising: number, fund: FundItem): number {
        if (fund.eltif) {
            return Utilities.getTakeUpRateValue(fund);
        } else {
            return Math.min(Utilities.getDiversificationRateValue(raising), Utilities.getTakeUpRateValue(fund));
        }
    },
    getFundMaxProportion: function (raising: number, fund: FundItem): number {
        const maxValue = Utilities.getFundMaxValue(raising, fund);
        return maxValue / raising;
    },
    getFullfillProportion: function (raising: number, funds: FundItem[]): number {
        let maxFundRaised = 0;
        const diversificationValue = Utilities.getDiversificationRateValue(raising);
        for (const fund of funds) {
            if (fund.eltif) {
                maxFundRaised += Utilities.getTakeUpRateValue(fund);
            } else {
                maxFundRaised += Math.min(Utilities.getTakeUpRateValue(fund), diversificationValue);
            }
        }
        if (maxFundRaised >= raising) {
            return 1;
        } else {
            return maxFundRaised / raising;
        }
    },
    canFulfillPortfolio: function (raising: number, funds: FundItem[]): boolean {
        let maxFundRaised = 0;
        const diversificationValue = Utilities.getDiversificationRateValue(raising);
        for (const fund of funds) {
            if (fund.eltif) {
                maxFundRaised += Utilities.getTakeUpRateValue(fund);
            } else {
                maxFundRaised += Math.min(Utilities.getTakeUpRateValue(fund), diversificationValue);
            }
            if (maxFundRaised >= raising) {
                return true;
            }
        }
        return false;
    },
    generateKey: function (): string {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789%#/!=+-.§~@$&';
        const charactersLength = characters.length;
        let counter = 0;
        while (counter < 32) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
            counter += 1;
        }
        return result;
    },
    copyToClipboard: function (str: string): void {
        const el = document.createElement("textarea");
        el.value = str;
        el.setAttribute("readonly", "");
        el.style.position = "absolute";
        el.style.left = "-9999px";
        document.body.appendChild(el);
        const selection = document.getSelection()
        const selected =
            selection !== null && selection.rangeCount > 0
                ? selection.getRangeAt(0)
                : false;
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        if (selected && selection) {
            selection.removeAllRanges();
            selection.addRange(selected);
        }
    }
};